/**
 * Customer-facing TruCoins ledger.
 *
 * All calls are gated by `requireSupabaseAuth`, so a caller can only read
 * their own summary + ledger; RLS on `loyalty_ledger` / `loyalty_accounts`
 * further restricts rows to `customer_id -> customers.auth_user_id = auth.uid()`.
 *
 * `visible=false` is returned whenever `loyalty_program_config.feature_enabled`
 * is off — the account UI must hide the TruCoins section entirely in that
 * case. The wire shape stays stable so a flag flip needs no UI redeploy.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { PENDING_EARN_COPY } from "@/lib/loyalty/earn";
import { isReferralEnabled } from "./referral-gate";

const PageInput = z.object({
  limit: z.number().int().min(1).max(50).default(20),
  offset: z.number().int().min(0).default(0),
});

export type LedgerEntry = {
  id: string;
  entryType: string;
  status: string;
  amountPaise: number;
  reason: string | null;
  orderId: string | null;
  referralId: string | null;
  awardAfter: string | null;
  expiresAt: string | null;
  createdAt: string;
  description: string;
  metadata: Record<string, string | number | boolean | null>;
};

export type ReferralSlab = {
  min: number;
  max: number | null;
  paise: number;
  current: boolean;
};

export type ReferralProgress = {
  successfulReferrals: number;
  nextSlabPaise: number | null;
  nextSlabAt: number | null; // referral count at which the next slab starts (null if on last slab)
  currentSlabPaise: number | null;
  /** Discount the referred friend gets on their first eligible order (config-driven). */
  friendDiscountPaise: number;
  slabs: ReferralSlab[];
};

export type LedgerPage = {
  visible: boolean;
  pendingCopy: string;
  summary: {
    availablePaise: number;
    pendingPaise: number;
    reservedPaise: number;
    lifetimeEarnedPaise: number;
    lifetimeRedeemedPaise: number;
    referralCode: string | null;
  } | null;
  referralProgress: ReferralProgress | null;
  entries: LedgerEntry[];
  total: number;
  limit: number;
  offset: number;
};

/** Pure slab picker mirroring loyalty_pick_referral_slab — used by the account UI. */
export function pickReferralSlab(
  slabs: Array<{ min: number; max: number | null; paise: number }>,
  count: number,
): { slabIndex: number; paise: number } {
  const n = count < 1 ? 1 : count;
  for (let i = 0; i < slabs.length; i++) {
    const s = slabs[i];
    if (n >= s.min && (s.max === null || n <= s.max)) {
      return { slabIndex: i, paise: s.paise };
    }
  }
  const last = slabs[slabs.length - 1];
  return { slabIndex: slabs.length - 1, paise: last?.paise ?? 0 };
}

/** English ordinal suffix — 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th", 23 → "23rd". */
export function ordinal(n: number): string {
  const abs = Math.abs(Math.trunc(n));
  const mod100 = abs % 100;
  const mod10 = abs % 10;
  const suffix =
    mod100 >= 11 && mod100 <= 13 ? "th" :
    mod10 === 1 ? "st" :
    mod10 === 2 ? "nd" :
    mod10 === 3 ? "rd" : "th";
  return `${n}${suffix}`;
}

/** Paise → "₹100" / "₹125" / "₹99.50". */
export function formatRupees(paise: number): string {
  const rupees = paise / 100;
  const whole = Number.isInteger(rupees) ? rupees.toFixed(0) : rupees.toFixed(2);
  return `₹${whole}`;
}

/** Human-readable label for a ledger row — used in the customer account UI. */
export function describeLedgerEntry(row: {
  entry_type: string;
  status: string;
  reason: string | null;
  referral_id: string | null;
  metadata: Record<string, unknown> | null;
}): string {
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  switch (row.entry_type) {
    case "earn_pending":
      return "TruCoins earn (pending delivery)";
    case "earn_available":
      return "TruCoins earned from order";
    case "redeem":
      return "Redeemed at checkout";
    case "redeem_reverse":
      return "Redemption released";
    case "refund_reverse":
      if (typeof meta.reason === "string" && meta.reason.includes("referral")) {
        return "Referral reversal";
      }
      return "Refund reversal";
    case "expire":
      return "TruCoins expired";
    case "referral_referrer": {
      const pos = Number(meta.slab_position);
      const paise = Number(meta.awarded_paise ?? meta.slab_paise);
      if (Number.isFinite(pos) && pos > 0 && Number.isFinite(paise) && paise > 0) {
        return `Referral reward, ${ordinal(pos)} successful referral (${formatRupees(paise)})`;
      }
      return "Referral reward (you referred a friend)";
    }
    case "referral_referred":
      return "Welcome bonus (a friend referred you)";
    case "review_bonus":
      return "Review reward";
    case "replenishment_bonus":
      return "Replenishment bonus";
    case "admin_adjust":
      return `Manual adjustment${row.reason ? `, ${row.reason}` : ""}`;
    default:
      return row.reason || row.entry_type;
  }
}

export const getMyTruCoinsLedger = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => PageInput.parse(raw ?? {}))
  .handler(async ({ data, context }): Promise<LedgerPage> => {
    const supabase = context.supabase;

    // Feature-flag gate — hide the whole tab until Ops turns it on.
    // Config is admin-only (no public SELECT policy); read with service role.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const cfgRes = await supabaseAdmin
      .from("loyalty_program_config")
      .select("feature_enabled, referral_enabled, referral_referrer_slabs, referral_friend_discount_paise")
      .eq("id", 1)
      .maybeSingle();

    const visible = !!cfgRes.data?.feature_enabled;
    if (!visible) {
      return {
        visible: false,
        pendingCopy: PENDING_EARN_COPY,
        summary: null,
        referralProgress: null,
        entries: [],
        total: 0,
        limit: data.limit,
        offset: data.offset,
      };
    }

    const [summaryRes, ledgerRes] = await Promise.all([
      supabase.rpc("loyalty_get_my_summary"),
      (supabase.rpc as unknown as (
        name: string,
        args?: Record<string, unknown>,
      ) => Promise<{ data: any[] | null; error: { message?: string } | null }>)(
        "loyalty_get_my_ledger",
        { _limit: data.limit, _offset: data.offset },
      ),
    ]);

    if (summaryRes.error) throw new Error(summaryRes.error.message);
    if (ledgerRes.error) throw new Error(ledgerRes.error.message);

    const s = Array.isArray(summaryRes.data) ? summaryRes.data[0] : summaryRes.data;
    const rows = (ledgerRes.data ?? []) as Array<{
      id: string;
      entry_type: string;
      status: string;
      amount_paise: number;
      reason: string | null;
      order_id: string | null;
      referral_id: string | null;
      award_after: string | null;
      expires_at: string | null;
      created_at: string;
      metadata: Record<string, unknown> | null;
      total_count: number | string;
    }>;

    // Compute referrer slab progress from the ledger the caller can already see.
    // Uses live rows so a reversed referral no longer counts toward the next slab.
    const validReferrerRewards = rows.filter(
      (r) => r.entry_type === "referral_referrer" && r.status === "available",
    ).length;
    const slabsCfg = ((cfgRes.data as any)?.referral_referrer_slabs ?? []) as Array<{
      min: number; max: number | null; paise: number;
    }>;
    const nextCount = validReferrerRewards + 1;
    const { paise: nextPaise } = pickReferralSlab(slabsCfg, nextCount);
    const currentPaise = validReferrerRewards > 0
      ? pickReferralSlab(slabsCfg, validReferrerRewards).paise
      : null;
    // Find where the next slab "step up" happens (null if already on the top slab).
    const currentIdx = pickReferralSlab(slabsCfg, nextCount).slabIndex;
    const nextSlabAt = slabsCfg[currentIdx + 1]?.min ?? null;
    const referralProgress: ReferralProgress = {
      successfulReferrals: validReferrerRewards,
      currentSlabPaise: currentPaise,
      nextSlabPaise: nextPaise,
      nextSlabAt,
      friendDiscountPaise: Number((cfgRes.data as any)?.referral_friend_discount_paise ?? 0),
      slabs: slabsCfg.map((s, i) => ({ ...s, current: i === currentIdx })),
    };

    return {
      visible: true,
      pendingCopy: PENDING_EARN_COPY,
      summary: s
        ? {
            availablePaise: s.available_balance_paise ?? 0,
            pendingPaise: s.pending_balance_paise ?? 0,
            reservedPaise: s.reserved_balance_paise ?? 0,
            lifetimeEarnedPaise: s.lifetime_earned_paise ?? 0,
            lifetimeRedeemedPaise: s.lifetime_redeemed_paise ?? 0,
            referralCode: isReferralEnabled(cfgRes.data) ? s.referral_code ?? null : null,
          }
        : null,
      referralProgress: isReferralEnabled(cfgRes.data) ? referralProgress : null,
      entries: rows.map((r) => ({
        id: r.id,
        entryType: r.entry_type,
        status: r.status,
        amountPaise: r.amount_paise,
        reason: r.reason,
        orderId: r.order_id,
        referralId: r.referral_id,
        awardAfter: r.award_after,
        expiresAt: r.expires_at,
        createdAt: r.created_at,
        description: describeLedgerEntry(r),
        metadata: (r.metadata ?? {}) as LedgerEntry["metadata"],
      })),
      total: Number(rows[0]?.total_count ?? 0),
      limit: data.limit,
      offset: data.offset,
    };
  });
